import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, KPITile, DataTable } from "../components/Common";
import { TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from "recharts";

function Delta({ v, suffix }) {
  const cls = v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-500";
  const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 ${cls} font-semibold`}>
      <Icon className="w-3.5 h-3.5" />
      {v > 0 ? "+" : ""}{v}{suffix || ""}
    </span>
  );
}

export default function ProgressIntelligence() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.students().then(({ data }) => {
      setStudents(data);
      const edwin = data.find((s) => s.first_name === "Edwin" && s.last_name === "Thom");
      if (edwin) setSelected(edwin.id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setData(null);
    api.studentProgress(selected)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div data-testid="progress-page">
      <PageHeader
        kicker="Student Progress Intelligence"
        title="Term-on-Term & Year-on-Year Comparison"
        subtitle="Compare archived reports to reveal growth, forecasts and areas of focus. Powered by GPT-5.2 executive analysis."
      />

      <div className="hl-card p-4 mb-6">
        <label className="overline block mb-1.5">Student</label>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
          data-testid="prog-student"
          className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">— select —</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.year_group}</option>)}
        </select>
      </div>

      {loading && <Loading label="Analysing academic history…" />}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPITile tone="dark" testId="prog-avg-current" label="Overall Average" value={`${data.kpis.average_current}%`} sub={`prev ${data.kpis.average_previous}%`} />
            <div className="hl-card p-5">
              <div className="overline">Average Change</div>
              <div className="mt-1 text-2xl font-display font-bold"><Delta v={data.kpis.average_delta} suffix="%" /></div>
            </div>
            <KPITile testId="prog-att-current" label="Attendance" value={`${data.kpis.attendance_current}%`} sub={`prev ${data.kpis.attendance_previous}%`} />
            <KPITile tone="dark" testId="prog-forecast" label="Forecast (next term)" value={`${data.kpis.forecast_average}%`} sub="linear projection" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="hl-card p-5" data-testid="prog-trend-chart">
              <div className="overline mb-2">Progress trend</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="period" fontSize={10} stroke="#94a3b8" />
                    <YAxis domain={[40, 100]} fontSize={10} stroke="#94a3b8" />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="overall_average" stroke="#D9531E" strokeWidth={2.5} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="attendance_pct" stroke="#002147" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="behaviour_score" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[color:var(--hl-orange)]" /> Average</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[color:var(--hl-navy)]" /> Attendance</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-600" /> Behaviour</span>
              </div>
            </div>

            <div className="hl-card p-5" data-testid="prog-subject-deltas">
              <div className="overline mb-2">Subject delta (this term vs previous)</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={data.subject_deltas} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" domain={[-20, 20]} fontSize={10} stroke="#94a3b8" />
                    <YAxis type="category" dataKey="subject" fontSize={10} stroke="#94a3b8" width={110} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="delta" radius={[0, 3, 3, 0]}>
                      {data.subject_deltas.map((d, i) => (
                        <Cell key={i} fill={d.delta >= 0 ? "#059669" : "#dc2626"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <Section title="AI Progress Commentary" testId="prog-ai">
            <div className="hl-card hl-ai-card p-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[color:var(--hl-orange)]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--hl-orange)] font-bold">GPT-5.2 Analysis</span>
              </div>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{data.ai_narrative}</div>
            </div>
          </Section>

          <Section title="Subject-by-subject change">
            <DataTable
              columns={[
                { key: "subject", label: "Subject" },
                { key: "previous", label: "Previous", render: (r) => <span className="mono">{r.previous}%</span> },
                { key: "current", label: "Current", render: (r) => <span className="mono">{r.current}%</span> },
                { key: "delta", label: "Change", render: (r) => <Delta v={r.delta} suffix="%" /> },
              ]}
              rows={data.subject_deltas}
              empty="No archived history yet."
            />
          </Section>
        </>
      )}
    </div>
  );
}
